import {
  Button,
  FileUpload,
  GroupEditorModal,
  Icon,
  Input,
  Modal,
  PageHeader,
  Pagination,
  ShipNoteModal,
  Table,
  VesselGroupValue,
} from 'app/components';
import { CountryFlag } from 'CountryFlag/CountryFlag';

import { Form, Formik } from 'formik';

import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyExportFleetQuery } from '../../api/ships';
import {
  useAddToFleetMutation,
  useRemoveFromFleetMutation,
  useAddFleetFromFileMutation,
  useAddVesselsToGroupMutation,
  useRemoveGroupFromVesselsMutation,
} from '../../api/fleet';
import { useGetVesselsGeoInfoMutation, useGetNotesListMutation, useLazyGetUserFleetQuery } from '../../api/facade';

import * as Yup from 'yup';
import moment from 'moment';
import { Row } from 'react-table';
import { UserI } from '../../utils/userSlice';
import { AddModal } from './FleetModals/AddModal';
import { FleetInfo, TargetCatalogEntryInfo } from 'ais-facade-api/api';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { FleetManagementPanel } from './FleetManagementPanel';
import { useLocalStorage, useOnClickOutside, useAlertHook, useFormatMoment } from 'app/hooks';
import { setSelectedMmsi, setSelectedRow } from 'app/utils/tableSlice';
import { getI18n, useTranslation } from 'react-i18next';
import { sortAreas, handleFileDownload } from 'app/helpers';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

import { FleetActionCell } from './FleetActionCell';
import { ShipNoteCell } from 'app/components/ShipNote';
import { AreasCell } from './AreasCell';
import { PortCell } from './PortCell';

import styles from './Fleet.module.scss';

interface InputValues {
  mmsi: string;
}
interface FleetI {
  user: UserI | null;
}
interface FleetSortingInterface {
  direction: 'ASC' | 'DESC' | 'default';
  by: string;
}

export const FleetPage = ({ user }: FleetI) => {
  const { t } = useTranslation();
  const language = useMemo(() => getI18n().language, [t]);

  const selectedFromTable = useAppSelector((state) => state.tableSlice.fromTable.mmsiArray);
  const [getFleet, { data: fleet, isFetching: getFleetFetching, isLoading: getFleetLoading }] = useLazyGetUserFleetQuery();
  const [getAreas] = useGetVesselsGeoInfoMutation({
    fixedCacheKey: 'get-areas-mutation',
  });
  const [getPort] = useGetVesselsGeoInfoMutation({
    fixedCacheKey: 'get-port-mutation',
  });

  const dispatch = useAppDispatch();
  const [removeFromFleet] = useRemoveFromFleetMutation();
  const [addToFleet, { isSuccess: addedToFleet }] = useAddToFleetMutation();
  const [addToGroup, addToGroupRequest] = useAddVesselsToGroupMutation();
  const [deleteGroup, deleteGroupRequest] = useRemoveGroupFromVesselsMutation();
  const [getNotes, notesRequest] = useGetNotesListMutation({
    fixedCacheKey: 'get-notes-mutation',
  });

  const [showModal, setShowModal] = useState<boolean>(false);
  const [showGroupsModal, setShowGroupsModal] = useState<boolean>(false);

  const [showFileModal, setShowFileModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [file, setFile] = useState<File>();

  const [getCSV] = useLazyExportFleetQuery();
  const [upload] = useAddFleetFromFileMutation();

  const loading = getFleetLoading || getFleetFetching;

  const { addAlert } = useAlertHook();

  const reg = new RegExp('^[0-9]+$');
  const modalValidationSchema = Yup.object().shape({
    mmsi: Yup.string().min(9).max(9).required().matches(reg),
  });
  const initialSort: FleetSortingInterface = {
    direction: 'DESC',
    by: 'RECEIVED_DATE',
  };
  const [sorting, setSorting] = useState<FleetSortingInterface>(initialSort);
  const [term, setTerm] = useLocalStorage<string>('fleetCatalogTerm', '');
  const filterFleet: FleetInfo[] | undefined = useMemo(() => {
    if (term.length === 1) {
      return fleet;
    }
    return fleet?.filter((vessel) => {
      const mmsiMatches = vessel?.mmsi?.toString().includes(term);
      const imoMatches =
        vessel?.registryData?.imo?.toString().includes(term) ?? vessel?.staticData?.imo?.toString().includes(term);
      const nameMatches =
        vessel?.registryData?.name?.toLowerCase().includes(term.toLowerCase()) ??
        vessel?.staticData?.name?.toLowerCase().includes(term.toLowerCase());
      return mmsiMatches || imoMatches || nameMatches;
    });
  }, [fleet, term]);

  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const tableData = useMemo(
    () => filterFleet?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filterFleet, currentPage, itemsPerPage],
  );

  useEffect(() => {
    if (user) {
      const fleetObjDefifnitons = tableData?.map((vessel) => `MMSI:${vessel.mmsi}`);
      if (fleetObjDefifnitons && fleetObjDefifnitons?.length > 0) {
        getNotes(fleetObjDefifnitons);
      }
    }
  }, [tableData, user]);

  useEffect(() => {
    if (user) {
      const points = tableData?.reduce<{ key: string; latitude: number; longitude: number }[]>((acc, vessel) => {
        if (vessel?.mmsi && vessel?.position?.latitude !== undefined && vessel?.position?.longitude !== undefined) {
          acc.push({
            key: vessel.mmsi.toString(),
            latitude: Number(vessel.position.latitude),
            longitude: Number(vessel.position.longitude),
          });
        }
        return acc;
      }, []);

      if (points && points?.length > 0) {
        getAreas({ body: { points }, type: ['IN_AREA'] });
        getPort({ body: { points }, type: ['IN_PORT'] });
      }
    }
  }, [tableData, user, language]);

  useEffect(() => {
    if (user && notesRequest.isUninitialized) {
      const fleetObjDefifnitons = tableData?.map((vessel) => `MMSI:${vessel.mmsi}`);
      if (fleetObjDefifnitons && fleetObjDefifnitons?.length > 0) {
        getNotes(fleetObjDefifnitons);
      }
    }
  }, [notesRequest.isUninitialized]);
  const fetchFleetData = async () => {
    if (user) {
      return getFleet({
        sortBy: sorting.direction === 'default' ? initialSort.by : sorting.by,
        direction: sorting.direction === 'default' ? initialSort.direction : sorting.direction,
      });
    }
  };
  useEffect(() => {
    setCurrentPage(1);
  }, [term]);

  useEffect(() => {
    if (user) {
      fetchFleetData();
      setCurrentPage(1);
    }
  }, [sorting, user]);

  useEffect(() => {
    if (user) {
      fetchFleetData();
    }
  }, [user, language]);

  useEffect(() => {
    if (addedToFleet) {
      fetchFleetData();
    }
  }, [addedToFleet]);

  useEffect(() => {
    if (addToGroupRequest.isSuccess) {
      fetchFleetData();
    }
  }, [addToGroupRequest]);

  useEffect(() => {
    if (deleteGroupRequest.isSuccess) {
      fetchFleetData();
    }
  }, [deleteGroupRequest]);

  const initialInputValue: InputValues = { mmsi: '' };
  const handleFileChange = (evt: ChangeEvent<HTMLInputElement>) => {
    if (!evt.target.files) {
      return;
    }

    setFile(evt.target.files[0]);
  };
  const handleUpload = () => {
    const user_id = user?.value.sub;
    if (file && user_id) {
      const formData = new FormData();
      formData.append('file', file);
      upload({ data: formData, user_id })
        .unwrap()
        .then(() => {
          fetchFleetData().then(() => setShowFileModal(false));
        })
        .catch((error) => {
          addAlert(error);
        });
    }

    if (!file) {
      addAlert({
        data: {
          title: 'Error',
          status: 404,
          detail: 'Error',
          messages: [{ description: t('NoFile'), type: 'error' }],
        },
      });
    }
  };
  const handleAdd = () => {
    setShowModal(true);
    setShowFileModal(false);
  };
  const handleCloseModal = () => {
    setShowModal(false);
  };
  const deleteModalToggle = () => {
    setShowDeleteModal(true);
  };

  const handleExport = () => {
    getCSV({})
      .unwrap()
      .then(handleFileDownload)
      .catch((error) => addAlert(error));
  };

  const handleGroupChange = (groupKey: string) => {
    const user_id = user?.value.sub;
    if (user_id) {
      addToGroup({
        key: groupKey,
        array: { mmsiArray: selectedFromTable },
      })
        .unwrap()
        .then()
        .catch((error) => {
          addAlert(error);
        });
    }
  };

  const handleDeleteGroup = () => {
    deleteGroup({ mmsiArray: selectedFromTable })
      .unwrap()
      .then()
      .catch((error) => {
        addAlert(error);
      });
  };

  const handleImport = () => {
    setShowFileModal(true);
    setShowModal(false);
  };

  const handleSubmit = (value: InputValues) => {
    const user_id = user?.value.sub;

    if (user_id) {
      addToFleet({ mmsiArray: [parseInt(value.mmsi)] }).then((response) => {
        if ('error' in response) {
          const error = response.error;
          addAlert(error);
        }
      });
    }

    setShowModal(false);
  };

  const handleDelete = () => {
    removeFromFleet({ mmsiArray: selectedFromTable }).then(() => {
      setShowDeleteModal(false);
      fetchFleetData();
      dispatch(setSelectedMmsi([]));
    });
  };

  const initialFilesValues = { files: '' };

  const modalImportComponent = (
    <Formik
      initialValues={initialFilesValues}
      validationSchema={Yup.object({
        files: Yup.mixed().test('is-file-of-correct-type', t('IncorrectCSVFileFormatError'), () => {
          let valid = true;

          if (file) {
            const type = file.type.split('/')[1];
            const validTypes = ['csv'];
            if (!validTypes.includes(type)) {
              valid = false;
            }
          }
          return valid;
        }),
      })}
      onSubmit={handleUpload}
    >
      <Form>
        <FileUpload name="files" onChange={(e) => handleFileChange(e)} file={file} />
        <div className={styles.buttons_group}>
          <Button variant="primary" label={t('ButtonLabelImport')} type="submit" />
          <Button variant="general" label={t('ButtonLabelDiscard')} type="reset" onClick={() => setShowFileModal(false)} />
        </div>
      </Form>
    </Formik>
  );

  const [modalMmsi, setModalMmsi] = useState<number | null>(null);
  const showNoteModalState = useState<boolean>(false);

  const columns = useMemo<any>(
    () => [
      {
        Header: 'MMSI',
        accessor: 'mmsi',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>
            {typeof ship.mmsi === 'number' ? (
              <Link to={`/vessels/${ship.mmsi}`} className={styles.link}>
                {ship.mmsi}
              </Link>
            ) : (
              <>&mdash;</>
            )}
          </>
        ),
        canSort: true,
        sortBy: 'MMSI',
      },
      {
        Header: 'Name',
        accessor: 'staticData.name',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>
            {typeof ship?.registryData?.name === 'string' ? (
              ship.registryData.name
            ) : typeof ship.staticData?.name === 'string' ? (
              ship.staticData.name
            ) : (
              <>&mdash;</>
            )}
          </>
        ),
        canSort: true,
        sortBy: 'NAME',
      },
      {
        Header: 'IMO',
        accessor: 'staticData.imo',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>
            {typeof ship?.registryData?.imo === 'number' ? (
              ship.registryData.imo
            ) : typeof ship.staticData?.imo === 'number' ? (
              ship.staticData.imo
            ) : (
              <>&mdash;</>
            )}
          </>
        ),
      },
      {
        Header: 'Flag',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>
            {ship.meta && ship.meta.countryName ? (
              <>
                {ship.meta.countryCode && <CountryFlag countryCode={ship.meta.countryCode?.toLocaleLowerCase()} />}
                {ship.meta.countryName} ({ship.meta.countryCode})
              </>
            ) : (
              <>&mdash;</>
            )}
          </>
        ),
        canSort: true,
        sortBy: 'COUNTRY',
      },
      {
        Header: 'Type',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>{ship.meta && ship.meta.type ? ship.meta.type : <>&mdash;</>}</>
        ),
        canSort: true,
        sortBy: 'TYPE',
      },
      {
        Header: 'NavStatus',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>{ship.meta && ship.meta.status ? ship.meta.status : <>&mdash;</>}</>
        ),
        canSort: true,
        sortBy: 'NAVIGATION_STATUS',
      },
      {
        Header: 'Speed',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>
            {ship.position && typeof ship.position.sog === 'number' ? (
              ship.position.sog.toFixed(2) + ` ${t('VesselSpeedUnit')}`
            ) : (
              <>&mdash;</>
            )}
          </>
        ),
      },
      {
        Header: 'DataObtained',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => {
          const formatMoment = useFormatMoment();

          return <>{ship.t ? formatMoment(moment(ship.t)) : <>&mdash;</>}</>;
        },
        canSort: true,
        sortBy: 'RECEIVED_DATE',
      },
      {
        Header: 'Group',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => (
          <>{ship.groups && ship.groups[0] ? <VesselGroupValue group={ship.groups[0]} /> : null}</>
        ),
        canSort: true,
        sortBy: 'GROUP_NAME',
      },
      {
        Header: 'Areas',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => {
          if (!ship.mmsi) return null;
          return <AreasCell mmsi={ship.mmsi} />;
        },
      },
      {
        Header: 'Port',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => {
          if (!ship.mmsi) return null;
          return <PortCell mmsi={ship.mmsi} />;
        },
      },
      {
        Header: 'Notes',
        Cell: ({ row: { original: ship } }: { row: Row<FleetInfo> }) => {
          if (!ship.mmsi) return null;
          return (
            <div className={styles.ship_note}>
              <ShipNoteCell mmsi={ship.mmsi} />
            </div>
          );
        },
      },
      {
        Header: <Icon name="more-icon" width="13px" height="3px" />,
        accessor: 'actionCell',
        width: 34,
        minWidth: 34,
        maxWidth: 34,
        disableSortBy: true,
        Cell: ({ row: { original: ship } }: { row: Row<TargetCatalogEntryInfo> }) => (
          <div className={clsx(styles.center_icon_cell, styles.action_cell)}>
            <FleetActionCell
              vessel={ship}
              setModalMmsi={(mmsi) => {
                setModalMmsi(mmsi);
                showNoteModalState[1](true);
              }}
            />
          </div>
        ),
      },
    ],
    [t],
  );
  const groupsRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(groupsRef, () => {
    setShowGroupsModal(false);
    dispatch(setSelectedRow(null));
  });

  return (
    <div className={styles.page}>
      <PageHeader title={t('FleetLabel')} iconName="catalog-fleet" size="24px" />
      {typeof modalMmsi === 'number' && <ShipNoteModal mmsi={modalMmsi} modalState={showNoteModalState} />}

      <Modal
        component={
          <AddModal
            initialInputValue={initialInputValue}
            modalValidationSchema={modalValidationSchema}
            handleCloseModal={handleCloseModal}
            handleSubmit={(values) => handleSubmit(values)}
          />
        }
        onClose={() => setShowModal(false)}
        headerTitle={t('ModalTitleAddToFleet')}
        isShown={showModal}
      />

      {showGroupsModal && (
        <GroupEditorModal
          innerRef={groupsRef}
          onClose={() => setShowGroupsModal(false)}
          onSuccess={() => {
            fetchFleetData();
          }}
        />
      )}

      <Modal
        component={
          <>
            <div className={styles.modal_subheader}>
              <Icon name="alert" size="28px" />

              {selectedFromTable.length === 1 ? (
                <span className={styles.alert_phrase}>{t('VesselWillBeDeleted', { mmsi: selectedFromTable })}</span>
              ) : (
                <span className={styles.alert_phrase}>{t('VesselsWillBeDeleted', { amount: selectedFromTable.length })}</span>
              )}
            </div>
            <div className={styles.modal_question}>{t('ModalDeleteFromFleetQuestion')}</div>

            <div className={styles.buttons_group}>
              <Button variant="general" label={t('ButtonLabelDelete')} type="submit" onClick={handleDelete} />
              <Button
                variant="general"
                label={t('ButtonLabelToDiscard')}
                type="reset"
                onClick={() => setShowDeleteModal(false)}
              />
            </div>
          </>
        }
        onClose={() => setShowDeleteModal(false)}
        headerTitle={t('ModalTitleDeleteFromFleet')}
        isShown={showDeleteModal}
      />

      <Modal
        onClose={() => setShowFileModal(false)}
        headerTitle={t('ModalTitleImportToFleet')}
        isShown={showFileModal}
        component={modalImportComponent}
      />
      <div className={styles.fleet_table}>
        <div className={styles.table_header}>
          <FleetManagementPanel
            handleAdd={handleAdd}
            handleExport={fleet?.length !== 0 ? handleExport : undefined}
            handleImport={handleImport}
            handleDelete={deleteModalToggle}
            handleEditGroups={() => setShowGroupsModal(true)}
            handleGroupChange={handleGroupChange}
            handleDeleteGroup={handleDeleteGroup}
            disabled={selectedFromTable.length === 0}
          />
          <div className={styles.search_container}>
            {`${t('FleetVesselsTotal')}:`} {fleet?.length || 0}
            <Input
              className={styles.table_search}
              type="search"
              onChange={(e) => {
                setTerm(e.target.value);
              }}
              placeholder={t('SearchPlaceholder')}
              value={term}
            />
          </div>
        </div>

        {loading && <span className={styles.loading}>{t('SearchingText')}</span>}

        {!loading && filterFleet && filterFleet.length > 0 && tableData && (
          <>
            <div className={styles.table_container}>
              <Table
                manualSort={true}
                data={tableData}
                columns={columns}
                withCheckbox
                sorting={sorting}
                setSorting={setSorting}
              />
            </div>
            <div className={styles.pagination_container}>
              <Pagination
                totalCount={filterFleet.length}
                currentPage={currentPage}
                pageChangeHandler={setCurrentPage}
                rowsPerPage={20}
              />
            </div>
          </>
        )}
        {!fleet || (fleet.length === 0 && <div>{t('NoVesselsInFleet')}</div>)}
      </div>
    </div>
  );
};
